import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { IMAGES } from "@/constants/site";
import { PROJECTS, STATUS_STYLES, slug } from "@/constants/projects";

const FILTERS = ["All", "Ready to Move", "Under Construction", "Resale"];

export default function Residences({ onEnquire }) {
  const [filter, setFilter] = useState("All");
  const visible = PROJECTS.filter((p) => filter === "All" || p.status === filter);

  return (
    <section id="residences" data-testid="residences-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-forest/70">The Projects</p>
            <h2 className="mt-4 max-w-xl font-serif text-2xl tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Every Shantigram address, one specialist
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-500">
            New bookings, under-construction launches and verified resale units — we cover the entire township.
          </p>
        </div>

        <div data-testid="project-filters" className="mt-10 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`project-filter-${slug(f)}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                filter === f
                  ? "border-forest bg-forest text-gold"
                  : "border-slate-200 bg-white text-slate-600 hover:border-forest/40"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                key={p.name}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                data-testid={`project-card-${slug(p.name)}`}
                className="group flex flex-col overflow-hidden rounded-lg border border-black/5 bg-cream"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.name} — Adani Shantigram, Ahmedabad`}
                    loading="lazy"
                    onError={(e) => {
                      if (e.currentTarget.src !== IMAGES.hero) e.currentTarget.src = IMAGES.hero;
                    }}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span
                    className={`absolute left-4 top-4 rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${STATUS_STYLES[p.status]}`}
                  >
                    {p.status}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl text-slate-900">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">{p.config}</p>
                  {p.sizes && <p className="mt-1 text-xs text-slate-400">{p.sizes}</p>}
                  {p.tags ? (
                    <ul className="mt-4 space-y-1.5">
                      {p.tags.map((t) => (
                        <li key={t} className="flex items-center gap-2 text-xs text-slate-600">
                          <span className="h-1 w-1 shrink-0 rounded-full bg-gold" />
                          {t}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-xs leading-relaxed text-slate-600">{p.desc}</p>
                  )}
                  <div className="mt-auto flex items-center justify-between border-t border-black/5 pt-4 mt-6">
                    <span className="text-sm font-bold text-forest">{p.price}</span>
                    <button
                      data-testid={`project-enquire-${slug(p.name)}`}
                      onClick={() => onEnquire(p.name)}
                      className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-forest transition-colors duration-200 hover:text-gold"
                    >
                      Enquire
                      <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
